import { postPlayNotify, postPlayerState } from "@api/player";
import type { Tracklist } from "@api/tracklists/types";
import { playerContext } from "@layouts/App/context/Player";
import type { PlayerStore } from "@layouts/App/context/Player/types";
import { createEffect, createSignal, on, useContext } from "solid-js";
import { createStore } from "solid-js/store";

const IGNORED_FEEDS: Partial<PlayerStore["feed"]["type"]>[] = [
	"BOOKMARKS_TRACKS",
	"DISCOVER_PODCASTS",
	"RADIO",
	"TRACKLIST_COMMENTS",
	"TRACK",
];
const useSaveState = () => {
	const { player } = useContext(playerContext);
	const [lastUpdateTime, setLastUpdateTime] = createSignal(-1);
	const [playingTracklist, setPLayingTracklist] = createStore<{ uuid: Tracklist["uuid"]; reported: boolean }>({
		uuid: "",
		reported: false,
	});
	const [notifyTimer, setNotifyTimer] = createSignal<NodeJS.Timeout>();

	const handleNotify = (uuid: Tracklist["uuid"]) => {
		if (playingTracklist.reported || !uuid) return;
		setPLayingTracklist({ uuid, reported: true });
		postPlayNotify(uuid);
	};

	const handleSetNotifyTimer = () => {
		const uuid = player.queue[player.track]?.tracklistUuid;

		if (!uuid) return setPLayingTracklist({ uuid: "", reported: false });
		if (playingTracklist.reported && playingTracklist.uuid === uuid) return;
		setPLayingTracklist({ uuid, reported: false });
		if (player.playing) setNotifyTimer(setTimeout(() => handleNotify(uuid), 5000));
	};

	createEffect(
		on(
			() => player.playing,
			(p) => {
				const now = new Date().getTime();
				if (!p) {
					setLastUpdateTime(now - lastUpdateTime());
					clearTimeout(notifyTimer());
				} else {
					setLastUpdateTime(now - lastUpdateTime());
					handleSetNotifyTimer();
				}
			},
		),
	);

	createEffect(
		on(
			() => player.track,
			() => {
				setLastUpdateTime(-1);
				handleSetNotifyTimer();
			},
		),
	);

	createEffect(
		on(
			() => player.time,
			(time) => {
				const track = player.queue[player.track];
				const now = new Date().getTime();
				if (!track) return setLastUpdateTime(-1);
				if (lastUpdateTime() === -1) return setLastUpdateTime(now);
				if (now - lastUpdateTime() < 10000 || time < 1) return;
				setLastUpdateTime(now);
				if (track.type === "TRACK" && !IGNORED_FEEDS.includes(player.feed.type))
					postPlayerState({
						tracklistUuid: track.tracklistUuid ?? "",
						time,
						trackId: track.id,
						trackPlatform: track.platform,
					});
			},
		),
	);
};
export default useSaveState;
